/**
 * Engine-hosted checkout LP for Buyer Intent.
 *
 * URL: /checkout/<slug>?email=...&first_name=...&last_name=...&cur=...&utm_*=...
 *
 * Renders the Spiffy checkout INLINE on the engine domain via Spiffy's
 * <spiffy-checkout url="..."> custom element. Adopts the proven pattern from
 * the TSBK-Spiffy HubSpot module (id 48857526153, 67+ pages):
 *
 *   1. Render an empty .spiffy-checkout-wrap div with data-url
 *   2. In useEffect (after React mounts the wrap):
 *      a. Inject SpiffyJS shim (if missing)
 *      b. Call spiffy.identify({email, first_name, last_name, utm_*}) — queued
 *      c. document.createElement('spiffy-checkout'), setAttribute, append
 *      d. spiffy.load(<account>) — injects spiffy.js script
 *   3. spiffy.js hydrates → scans DOM → finds our element → renders embed
 *      with prefilled fields from the queued identify() call
 *
 * **Critical ordering**: spiffy.load() MUST run AFTER element creation. If
 * SpiffyJS loads before the element exists, its initial DOM scan finds nothing
 * and never sees the element. (We learned this the hard way 2026-04-20 by
 * loading the SpiffyJS shim in <head> — embed rendered blank.)
 *
 * Every other param on the engine LP URL (promo `code`, UTMs, click ids, ref)
 * is also appended to the spiffy_checkout_url, so a code pre-applies and
 * attribution lands on Spiffy's order record.
 *
 * Phase 2 Summit additions (docs/summit-migration/PHASE2-CONTRACTS.md §6):
 *   • Multi-currency: resolveEventCurrency (lib/geoCurrency) — the SAME rule
 *     the pricingTiers block uses. The event sells only in the currencies its
 *     tier's `checkoutUrls` carries; `?cur=` or the visitor's geo currency
 *     applies only when the event sells in it, else the checkout's own base
 *     currency. The AU Summit's tiers carry the PB Australia (AUD) checkout
 *     alone, so every AU buyer is charged AUD by PB Australia — a hand-edited
 *     `?cur=USD` or a US IP cannot move the charge to TSB Global's account,
 *     because the server never hands this page a USD URL for that event
 *     (mct_a9303307-0e5, 2026-09-30). Non-tier intents keep the legacy
 *     `spiffy_checkout_url`.
 *   • spiffy.load() account is parsed from the chosen URL's `<account>.spiffy.co`
 *     subdomain (each currency lives in its own Spiffy account) — no longer
 *     hardcoded 'purebkusa' (kept only as the parse fallback).
 *   • Email-first arrival (no `?email=` prefill — e.g. from the Summit
 *     pricingTiers CTAs): the page hosts the buyer-intent capture step itself.
 *     POST /api/buyer-intent/capture; if the response says `memberVip: true`
 *     the Spiffy embed is NEVER mounted — a member panel renders instead with
 *     a button to the capture response's `registerUrl` (free registration,
 *     VIP applies automatically).
 *
 * Per docs/BUYER_INTENT_v1.md.
 */

import { spiffyUrlString, spiffyPrefillName, withoutSpacedNameParams, withPagePassthrough } from './lib/spiffyUrl';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SUPPORT_EMAIL, BRAND_NAME, BRAND_HOME_URL, BRAND_LOGO_URL } from './branding';
import BrokenPage from './components/BrokenPage';
import { PolicyLinks } from './components/PolicyLinks';
import { getAttributionPayload, stampVisitorOnAddress } from './attribution';
import {
  fetchGeoCountry,
  resolveEventCurrency,
  offeredCheckoutCurrencies,
  checkoutBaseCurrency,
} from './lib/geoCurrency';
import { fetchWithRetry } from './lib/fetchRetry';
import { useBrandGtm } from './lib/useBrandGtm';
import { useOrderGate, withGateParams, type ClaimOutcome } from './lib/useOrderGate';
import { OrderGatePanel, CreditClaimedPanel } from './components/OrderGatePanel';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface BuyerIntentConfigPublic {
  slug: string;
  title: string;
  modal_headline: string;
  modal_subheadline: string | null;
  fields: Array<'email' | 'first_name' | 'last_name'>;
  submit_label: string;
  spiffy_checkout_url: string;
  intent_label: string;
  product_value_cents: number | null;
  /** Per-currency Spiffy checkout URLs (contract §3d). Key = ISO code
   *  ('USD','CAD','AUD'). Absent/empty on legacy single-account intents. */
  checkoutUrls?: Record<string, string> | null;
  /** Per-currency display pricing (contract §3d). Informational — Spiffy's
   *  own embed shows the authoritative price. */
  pricing?: Record<string, { amount: number; wasAmount?: number }> | null;
  /** The one currency the server says this visitor is shown (CAD in CA, AUD
   *  in AU/NZ/PH, else USD — worker/src/visitor-currency.ts). Only set for
   *  intents carrying their own config-level `checkoutUrls` (price card v2);
   *  null for event-tier intents, which resolve currency client-side. */
  visitorCurrency?: string | null;
  /** Appended to the per-currency price chip, e.g. " per month". */
  price_suffix?: string | null;
  /** Display-only price line rendered on the pre-checkout capture card
   *  (e.g. "$29/month"). Cosmetic — Spiffy's own embed remains authoritative. */
  price_display?: string | null;
  /** "What's included" bullets rendered on the pre-checkout capture card,
   *  before the visitor ever sees Spiffy's own embed. */
  included?: string[] | null;
  /** One-line reassurance/guarantee shown under the submit button. */
  reassurance?: string | null;
  /** Opt-in dark theme (magenta/blue on navy — the Ignite event palette) for
   *  the page chrome around the capture card. Null/absent = plain TSB look.
   *  See worker/src/types.ts BuyerIntentConfig.pageTheme doc comment. */
  pageTheme?: 'ignite' | null;
  /** Brand/program-aware logo, resolved server-side (2026-09-18) via the same
   *  resolveBrandForAssessment() every other public route uses — a program-
   *  linked checkout (e.g. clients-program, freedom-gateway-program) gets that
   *  program's own logo; TSB Member (no program link) gets the parent brand's
   *  logo. Null only if brand resolution genuinely found nothing — fall back
   *  to the generic TSB mark in that case, never render a blank header. */
  logoUrl?: string | null;
  logoLinkUrl?: string | null;
  brandName?: string | null;
  /** Set only on a printed-book checkout (config printBook, e.g. tsbk). Says,
   *  before payment, whether this visitor's country gets the ebook instead of
   *  a printed copy (worker/src/buyer-intent-routes.ts). */
  bookShipping?: {
    printCountries: string[];
    visitorCountry: string | null;
    visitorGetsEbook: boolean;
    ebookPath: string | null;
  } | null;
  /** A time-boxed offer the link named (?offer=<key> or ?code=<code>),
   *  resolved server-side (worker/src/checkout-offers.ts), e.g. the NA Summit
   *  booth price. Null when the link names none or it has ended. */
  offer?: CheckoutOfferPublic | null;
}

export interface CheckoutOfferPublic {
  key: string;
  /** Currencies it is valid in; null = every currency. */
  currencies: string[] | null;
  /** Spiffy promo code to put on the embed, if the offer is a price. */
  code: string | null;
  headline: string;
  detail: string;
  pricing: Record<string, { amount: number; wasAmount?: number }> | null;
  endsAt: string;
}

/** The offer applies to this visitor's checkout only in a currency it names:
 *  its code exists only in those Spiffy accounts. */
export function offerForCurrency(offer: CheckoutOfferPublic | null | undefined, currency: string | null): CheckoutOfferPublic | null {
  if (!offer) return null;
  if (!offer.currencies) return offer;
  return currency && offer.currencies.includes(currency) ? offer : null;
}

const PRINT_COUNTRY_NAMES: Record<string, string> = {
  US: 'the United States', CA: 'Canada', AU: 'Australia', NZ: 'New Zealand', GB: 'the United Kingdom',
};

function countryList(codes: string[]): string {
  const names = codes.map((c) => PRINT_COUNTRY_NAMES[c] ?? c);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join('');
}

/** Capture response — legacy snake_case fields plus the contract §3d member
 *  fields. Snake_case aliases accepted defensively. */
interface CaptureResponse {
  capture_id?: string;
  redirect_url?: string;
  intent_label?: string;
  memberVip?: boolean;
  member_vip?: boolean;
  registerUrl?: string;
  register_url?: string;
  error?: string;
}

declare global {
  interface Window {
    spiffy?: {
      identify: (traits: Record<string, unknown>) => void;
      config: (opts: Record<string, unknown>) => void;
      load?: (account: string, domain?: string) => void;
      [k: string]: unknown;
    };
  }
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/** Fallback Spiffy account when the checkout URL isn't a *.spiffy.co URL. */
const DEFAULT_SPIFFY_ACCOUNT = 'purebkusa';

/** Parse the Spiffy account from the checkout URL's `<account>.spiffy.co`
 *  subdomain. Each currency's checkout lives in its own Spiffy account
 *  (US purebkusa / CA / AU), so the account must follow the chosen URL. */
export function spiffyAccountFromUrl(spiffyUrl: string): string {
  try {
    const host = new URL(spiffyUrl).hostname.toLowerCase();
    const m = host.match(/^([a-z0-9-]+)\.spiffy\.co$/);
    if (m && m[1] !== 'www' && m[1] !== 'js') return m[1];
  } catch {
    /* fall through */
  }
  return DEFAULT_SPIFFY_ACCOUNT;
}

/** Append contact prefill + UTMs onto the Spiffy checkout URL.
 *  Per Spiffy docs (university.spiffy.co/quick-find/populating-fields-selecting-
 *  options-via-url-parameters), prefill is URL-driven — not via spiffy.identify():
 *    - email      → ?email=
 *    - first name → ?name_first=
 *    - last name  → ?name_last=
 *  Plus every other link param (promo `code`, UTMs, click ids, ref), so a
 *  booth offer's code pre-applies and attribution reaches the order record. */
function buildSpiffyEmbedUrl(
  spiffyUrl: string,
  email: string,
  firstName: string,
  lastName: string,
  params: URLSearchParams,
  offerCode: string | null = null,
): string {
  if (!spiffyUrl) return '';
  let out: URL;
  try {
    out = new URL(spiffyUrl);
  } catch {
    return spiffyUrl;
  }
  if (email) out.searchParams.set('email', email);
  // A spaced name is left off: Spiffy's script turns spaces into "+" (spiffyUrl.ts).
  const fn = spiffyPrefillName(firstName);
  const ln = spiffyPrefillName(lastName);
  if (fn) out.searchParams.set('name_first', fn);
  if (ln) out.searchParams.set('name_last', ln);
  // Everything else on the link — the promo `code`, UTMs, click ids, ref.
  withPagePassthrough(out, params);
  // An offer named by key (?offer=) carries its own code; never one this
  // currency's account lacks (offerForCurrency decided that).
  if (offerCode) out.searchParams.set('code', offerCode);
  return spiffyUrlString(out);
}

/** Merge an ignite overlay onto a base inline-style object when the checkout
 *  opts into the dark theme (`cfg.pageTheme === 'ignite'`), else pass the
 *  base through untouched. Kept trivial on purpose — this is CheckoutPage's
 *  own small conditional-style mechanism, deliberately independent of the
 *  blocks-events.css `.tsb-evt-theme-ignite` class tree (see the pageTheme
 *  doc comment on BuyerIntentConfig in worker/src/types.ts for why). */
function themed<T extends React.CSSProperties>(base: T, overlay: Partial<T>, active: boolean): T {
  return active ? { ...base, ...overlay } : base;
}

/** `$45` for whole amounts, `$64.50` when the price carries cents — mirrors
 *  taker/src/components/Blocks.tsx's fmtDollars() exactly (kept as its own
 *  copy rather than an import: that module isn't otherwise a CheckoutPage
 *  dependency, and this is a single three-line pure function). */
function fmtDollars(amount: number): string {
  const cents = !Number.isInteger(amount);
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/** Real per-currency price for the pre-checkout price chip, computed from the
 *  same `pricing` map the eventTierRef resolves (contract §3d) — never a
 *  hardcoded figure, so `?cur=CAD` (or geo) always shows the currency the
 *  visitor will actually be charged in. Falls back to the config's static
 *  `price_display` string only while `pricing`/`currency` aren't available
 *  yet (e.g. geo detection still in flight) or for non-event-tier intents
 *  (TSB Member) that carry no `pricing` map at all. */
function priceLabelFor(
  pricing: Record<string, { amount: number; wasAmount?: number }> | null | undefined,
  currency: string | null,
  fallback: string | null,
  suffix?: string | null,
): string | null {
  if (pricing && currency && pricing[currency]) {
    const entry = pricing[currency];
    const was = entry.wasAmount != null ? `, was ${fmtDollars(entry.wasAmount)}` : '';
    return `${fmtDollars(entry.amount)} ${currency}${suffix ?? ''}${was}`;
  }
  return fallback ?? null;
}

export default function CheckoutPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const urlEmail = params.get('email') ?? '';
  const urlFirstName = params.get('first_name') ?? '';
  const urlLastName = params.get('last_name') ?? '';
  const curParam = (params.get('cur') ?? '').trim().toUpperCase();

  // Brand-aware GTM — buyer-intent configs carry no brand payload; the
  // checkout is the deepest funnel step, so inject via the host fallback.
  useBrandGtm();

  const [cfg, setCfg] = useState<BuyerIntentConfigPublic | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // ── Visitor country for the currency decision below. undefined = geo still
  //    in flight. One module-cached fetch; resolves null on failure. ─────────
  const [geoCountry, setGeoCountry] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    fetchGeoCountry().then((country) => {
      if (!cancelled) setGeoCountry(country);
    });
    return () => { cancelled = true; };
  }, []);

  // ── Capture state. Arriving with ?email= means the buyer-intent modal
  //    already captured upstream — go straight to the embed. Otherwise the
  //    page hosts the capture step itself. ──────────────────────────────────
  const [captured, setCaptured] = useState<{ email: string; firstName: string; lastName: string } | null>(
    urlEmail ? { email: urlEmail, firstName: urlFirstName, lastName: urlLastName } : null,
  );
  const [memberVip, setMemberVip] = useState<{ registerUrl: string | null } | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        // The link's offer key / code go to the server, which decides whether
        // an offer applies (checkout-offers.ts).
        const q = new URLSearchParams();
        const offerParam = params.get('offer');
        const codeParam = params.get('code');
        if (offerParam) q.set('offer', offerParam);
        if (codeParam) q.set('code', codeParam);
        const qs = q.toString() ? `?${q}` : '';
        const res = await fetchWithRetry(`${API}/api/buyer-intent/${encodeURIComponent(slug)}${qs}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`config ${res.status}`);
        const data = (await res.json()) as BuyerIntentConfigPublic;
        if (cancelled) return;
        setCfg(data);
      } catch (err) {
        if (!cancelled) setError('We couldn\'t load this checkout. Please go back and try again.');
        // eslint-disable-next-line no-console
        console.error('[checkout] config fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Entitlement-aware order gate (build F) — runs once the buyer's email is
  // known. Never for the member-VIP panel (that never mounts Spiffy).
  const gate = useOrderGate({ email: captured?.email ?? '', offerType: 'intent', offerRef: slug, enabled: !!captured && !memberVip });
  const [claimed, setClaimed] = useState<Extract<ClaimOutcome, { ok: true }> | null>(null);
  // A printed-book checkout whose visitor is outside the print countries sees
  // the ebook notice first (mct_685e5714-197). ?ship=print, or the link on
  // the notice, says they are shipping to a print country after all.
  const [shipPrint, setShipPrint] = useState(params.get('ship') === 'print');

  if (error) {
    return <BrokenPage variant="error" message={error} />;
  }

  if (!cfg) {
    return (
      <main style={pageStyle}>
        <div style={cardStyle}>
          <p style={{ color: '#64748B', fontSize: 15, textAlign: 'center', margin: 0 }}>Loading…</p>
        </div>
      </main>
    );
  }

  // Opt-in dark theme (Summit's Ignite palette) — see BuyerIntentConfig.pageTheme
  // doc comment (worker/src/types.ts). Only known once `cfg` has loaded, so the
  // brief pre-fetch "Loading…" screen above always renders in the plain look —
  // acceptable, it lasts a fraction of a second and never shows real content.
  const isIgnite = cfg.pageTheme === 'ignite';

  // ── Currency: the SAME rule the pricingTiers block uses — only ever one the
  //    event sells in (see resolveEventCurrency). null while geo is in flight
  //    and the event sells in more than one currency. ─────────────────────
  const offeredCurrencies = offeredCheckoutCurrencies(cfg.checkoutUrls);
  // A server-resolved visitorCurrency (config-level checkoutUrls intents)
  // stands in for `?cur=` when none was passed — it arrives with the config,
  // so there is no geo wait and an unknown country is already USD.
  const currency = resolveEventCurrency({
    requested: curParam || cfg.visitorCurrency || null,
    country: geoCountry,
    offered: offeredCurrencies,
    base: checkoutBaseCurrency(cfg.checkoutUrls, cfg.spiffy_checkout_url),
  });
  const offer = offerForCurrency(cfg.offer, currency);

  // ── Printed book, visitor outside the print countries: say so BEFORE they
  //    pay for shipping. A printed copy only goes to the print countries;
  //    everyone else gets the ebook, free, with nothing to pay. ─────────────
  const book = cfg.bookShipping;
  if (book?.visitorGetsEbook && !shipPrint) {
    return (
      <main style={themed(pageStyle, ignitePageStyle, isIgnite)}>
        <div style={themed(topBarStyle, igniteTopBarStyle, isIgnite)}>
          <a href={cfg.logoLinkUrl ?? BRAND_HOME_URL} style={logoLinkStyle} aria-label={cfg.brandName ?? BRAND_NAME}>
            <img src={cfg.logoUrl ?? BRAND_LOGO_URL} alt={cfg.brandName ?? BRAND_NAME} style={logoImgStyle} />
          </a>
        </div>
        <div style={cardStyle} data-testid="book-ebook-notice">
          <h1 style={memberHeadingStyle}>Where you are, the book comes as an ebook.</h1>
          <p style={memberBodyStyle}>
            We can only post printed copies to {countryList(book.printCountries)}. Everywhere
            else, you get the ebook instead, so there&rsquo;s no shipping to pay.
          </p>
          {book.ebookPath ? (
            <a href={book.ebookPath} style={memberCtaStyle}>Get the free ebook</a>
          ) : (
            <p style={memberBodyStyle}>
              Write to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we&rsquo;ll send you the ebook.
            </p>
          )}
          <p style={{ ...memberBodyStyle, marginTop: 20, marginBottom: 0, fontSize: 14 }}>
            Shipping to one of those countries?{' '}
            <button
              type="button"
              onClick={() => setShipPrint(true)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
            >
              Continue to the printed copy
            </button>
          </p>
        </div>
        <footer style={themed(footStyle, igniteFootStyle, isIgnite)}>
          Need help? <a href={`mailto:${SUPPORT_EMAIL}`} style={themed(footLinkStyle, igniteFootLinkStyle, isIgnite)}>{SUPPORT_EMAIL}</a>
          <div style={{ marginTop: 8 }}><PolicyLinks /></div>
        </footer>
      </main>
    );
  }

  // ── Member panel — the capture response said this email's membership
  //    already includes VIP. NEVER mount the Spiffy embed in this state. ────
  if (memberVip) {
    return (
      <main style={themed(pageStyle, ignitePageStyle, isIgnite)}>
        <div style={themed(topBarStyle, igniteTopBarStyle, isIgnite)}>
          <a href={cfg.logoLinkUrl ?? BRAND_HOME_URL} style={logoLinkStyle} aria-label={cfg.brandName ?? BRAND_NAME}>
            <img src={cfg.logoUrl ?? BRAND_LOGO_URL} alt={cfg.brandName ?? BRAND_NAME} style={logoImgStyle} />
          </a>
        </div>
        <div style={cardStyle}>
          <h1 style={memberHeadingStyle}>You&rsquo;re a TSB Member — VIP access is included.</h1>
          <p style={memberBodyStyle}>
            Your membership already covers VIP access to this event, so there&rsquo;s nothing
            to pay here. Register free with your member email and VIP applies automatically.
          </p>
          {memberVip.registerUrl ? (
            <a href={memberVip.registerUrl} style={memberCtaStyle}>Register free — VIP included</a>
          ) : (
            <p style={{ ...memberBodyStyle, marginBottom: 0 }}>
              Head back to the event page to register with your member email.
            </p>
          )}
        </div>
        <footer style={themed(footStyle, igniteFootStyle, isIgnite)}>
          Need help? <a href={`mailto:${SUPPORT_EMAIL}`} style={themed(footLinkStyle, igniteFootLinkStyle, isIgnite)}>{SUPPORT_EMAIL}</a>
          <div style={{ marginTop: 8 }}><PolicyLinks /></div>
        </footer>
      </main>
    );
  }

  // ── Page-hosted email capture (no ?email= prefill). ──────────────────────
  if (!captured) {
    return (
      <main style={themed(pageStyle, ignitePageStyle, isIgnite)}>
        <div style={themed(topBarStyle, igniteTopBarStyle, isIgnite)}>
          <a href={cfg.logoLinkUrl ?? BRAND_HOME_URL} style={logoLinkStyle} aria-label={cfg.brandName ?? BRAND_NAME}>
            <img src={cfg.logoUrl ?? BRAND_LOGO_URL} alt={cfg.brandName ?? BRAND_NAME} style={logoImgStyle} />
          </a>
        </div>
        <CaptureCard
          cfg={cfg}
          currency={currency}
          offer={offer}
          isIgnite={isIgnite}
          onMemberVip={(registerUrl) => setMemberVip({ registerUrl })}
          onCaptured={(lead) => setCaptured(lead)}
        />
        <footer style={themed(footStyle, igniteFootStyle, isIgnite)}>
          Need help? <a href={`mailto:${SUPPORT_EMAIL}`} style={themed(footLinkStyle, igniteFootLinkStyle, isIgnite)}>{SUPPORT_EMAIL}</a>
          <div style={{ marginTop: 8 }}><PolicyLinks /></div>
        </footer>
      </main>
    );
  }

  // ── Pick the per-currency checkout URL. Missing/placeholder entries fall
  //    back to the legacy spiffy_checkout_url (the USD checkout, per the
  //    contract's external-dependency note). When the config is multi-currency
  //    and the currency hasn't resolved yet (geo in flight), hold the embed —
  //    the Spiffy account is baked into spiffy.load() at mount and cannot be
  //    swapped afterwards. ───────────────────────────────────────────────────
  const hasMultiCurrency = offeredCurrencies.length > 0;
  if (hasMultiCurrency && !currency) {
    return (
      <main style={themed(pageStyle, ignitePageStyle, isIgnite)}>
        <div style={cardStyle}>
          <p style={{ color: '#64748B', fontSize: 15, textAlign: 'center', margin: 0 }}>Preparing your checkout…</p>
        </div>
      </main>
    );
  }

  const chosenCheckoutUrl =
    (hasMultiCurrency && currency && cfg.checkoutUrls?.[currency]) || cfg.spiffy_checkout_url;
  const spiffyAccount = spiffyAccountFromUrl(chosenCheckoutUrl);
  const spiffyUrl = buildSpiffyEmbedUrl(
    chosenCheckoutUrl,
    captured.email,
    captured.firstName,
    captured.lastName,
    params,
    offer?.code ?? null,
  );

  // Engine LP is intentionally minimal — Spiffy's embedded form already shows the
  // product label, value, headline, and trust strip. Adding our own header above
  // it just duplicates content. Keep page chrome to a thin TSB wordmark + footer.
  return (
    <main style={themed(pageStyle, ignitePageStyle, isIgnite)}>
      <div style={themed(topBarStyle, igniteTopBarStyle, isIgnite)}>
        <a href={cfg.logoLinkUrl ?? BRAND_HOME_URL} style={logoLinkStyle} aria-label={cfg.brandName ?? BRAND_NAME}>
          <img src={cfg.logoUrl ?? BRAND_LOGO_URL} alt={cfg.brandName ?? BRAND_NAME} style={logoImgStyle} />
        </a>
      </div>
      <div style={shellStyle}>
        {/*
          Spiffy embed — adopts the proven pattern from the TSBK-Spiffy HubSpot
          module (id 48857526153, 67+ pages). Render an empty wrap div with
          data-url; useEffect creates the <spiffy-checkout> element programmatically
          and appends. spiffy.load() runs LAST so the element is in the DOM when
          SpiffyJS scans. NOTE: this white embed card is Spiffy's own hosted
          form — out of our control and NOT themed even when isIgnite is true;
          only the surrounding engine chrome (background/header/footer) carries
          the dark palette here.
        */}
        {offer && !claimed && <OfferBanner offer={offer} />}
        {claimed ? (
          <CreditClaimedPanel outcome={claimed} email={captured.email} dark={isIgnite} />
        ) : (
          <>
            <OrderGatePanel offers={gate.offers} onClaim={gate.claim} onAccept={gate.accept} onClaimed={setClaimed} dark={isIgnite} />
            {gate.ready ? (
              <>
                <div
                  ref={wrapRef}
                  className="spiffy-checkout-wrap"
                  data-url={spiffyUrl}
                  style={embedWrapStyle}
                />
                <SpiffyEmbedMount
                  wrapRef={wrapRef}
                  url={withGateParams(spiffyUrl, gate.checkoutParams)}
                  account={spiffyAccount}
                  identifyTraits={buildIdentifyTraits(captured.email, captured.firstName, captured.lastName, params)}
                />
              </>
            ) : (
              <p style={{ color: '#64748B', fontSize: 15, textAlign: 'center' }}>Preparing your checkout…</p>
            )}
          </>
        )}
      </div>
      <footer style={themed(footStyle, igniteFootStyle, isIgnite)}>
        Need help? <a href={`mailto:${SUPPORT_EMAIL}`} style={themed(footLinkStyle, igniteFootLinkStyle, isIgnite)}>{SUPPORT_EMAIL}</a>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>
    </main>
  );
}

/** Page-hosted buyer-intent capture card. Shown when the visitor lands on
 *  /checkout/<slug> without an ?email= prefill (e.g. straight from a Summit
 *  pricingTiers CTA). POSTs the same /api/buyer-intent/capture the modal uses;
 *  the response's memberVip/registerUrl (contract §3d) decides whether the
 *  Spiffy embed or the member panel renders next. */
function CaptureCard({
  cfg,
  currency,
  offer,
  isIgnite,
  onMemberVip,
  onCaptured,
}: {
  cfg: BuyerIntentConfigPublic;
  /** Resolved display currency (`?cur=` or geo) — null while geo is still in
   *  flight. Drives the real per-currency price chip via `cfg.pricing`. */
  currency: string | null;
  /** The link's offer, already checked against this currency. */
  offer: CheckoutOfferPublic | null;
  /** Opt-in dark theme — see `themed()`/`pageTheme` doc comments above. */
  isIgnite: boolean;
  onMemberVip: (registerUrl: string | null) => void;
  onCaptured: (lead: { email: string; firstName: string; lastName: string }) => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pending, setPending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fields = cfg.fields ?? ['email', 'first_name', 'last_name'];
  const wantsFirst = fields.includes('first_name');
  const wantsLast = fields.includes('last_name');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setPending(true);
    setFormError(null);
    try {
      const res = await fetch(`${API}/api/buyer-intent/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: cfg.slug,
          email: trimmedEmail,
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
          source_page: window.location.href,
          attribution: getAttributionPayload(),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as CaptureResponse;
      if (!res.ok) {
        setFormError(data.error || 'Something went wrong. Please try again in a moment.');
        setPending(false);
        return;
      }
      if (data.memberVip === true || data.member_vip === true) {
        onMemberVip(data.registerUrl ?? data.register_url ?? null);
        return;
      }
      onCaptured({ email: trimmedEmail, firstName: firstName.trim(), lastName: lastName.trim() });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[checkout] capture failed', err);
      setFormError('Network error. Please check your connection and try again.');
      setPending(false);
    }
  }

  // An offer's own price (e.g. the Summit $199) replaces the list price.
  const offerPriced = !!(offer?.pricing && currency && offer.pricing[currency]);
  const priceLabel = offerPriced
    ? priceLabelFor(offer!.pricing, currency, null, cfg.price_suffix)
    : priceLabelFor(cfg.pricing, currency, cfg.price_display ?? null, cfg.price_suffix);

  return (
    <div style={cardStyle}>
      {offer && <OfferBanner offer={offer} />}
      {priceLabel && <div style={themed(capturePriceChipStyle, igniteCapturePriceChipStyle, isIgnite)} data-testid="checkout-price">{priceLabel}</div>}
      <h1 style={captureHeadingStyle}>{cfg.modal_headline || `Continue to checkout — ${cfg.intent_label}`}</h1>
      {cfg.modal_subheadline && <p style={captureSubheadStyle}>{cfg.modal_subheadline}</p>}
      {cfg.included && cfg.included.length > 0 && (
        <ul style={captureIncludedListStyle}>
          {cfg.included.map((item, i) => (
            <li key={i} style={captureIncludedItemStyle}>
              <span style={themed(captureIncludedCheckStyle, igniteCaptureIncludedCheckStyle, isIgnite)} aria-hidden="true">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <label htmlFor="checkout-email" style={captureLabelStyle}>Email</label>
        <input
          id="checkout-email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={pending}
          style={captureInputStyle}
        />
        {wantsFirst && (
          <>
            <label htmlFor="checkout-first-name" style={captureLabelStyle}>First name</label>
            <input
              id="checkout-first-name"
              type="text"
              autoComplete="given-name"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={pending}
              style={captureInputStyle}
            />
          </>
        )}
        {wantsLast && (
          <>
            <label htmlFor="checkout-last-name" style={captureLabelStyle}>Last name</label>
            <input
              id="checkout-last-name"
              type="text"
              autoComplete="family-name"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={pending}
              style={captureInputStyle}
            />
          </>
        )}
        {formError && <div role="alert" style={captureErrorStyle}>{formError}</div>}
        <button type="submit" disabled={pending || !email.trim()} style={themed(captureSubmitStyle, igniteCaptureSubmitStyle, isIgnite)}>
          {pending ? 'One moment…' : (cfg.submit_label || 'Continue →')}
        </button>
      </form>
      {cfg.reassurance && <p style={captureReassuranceStyle}>{cfg.reassurance}</p>}
    </div>
  );
}

/** The link's offer, stated before the buyer types anything and again above
 *  Spiffy's form. */
export function OfferBanner({ offer }: { offer: CheckoutOfferPublic }) {
  return (
    <div style={offerBannerStyle} data-testid="checkout-offer" role="note">
      <div style={offerBannerHeadStyle}>{offer.headline}</div>
      <div style={offerBannerDetailStyle}>{offer.detail}</div>
    </div>
  );
}

const offerBannerStyle: React.CSSProperties = {
  background: '#ECFDF5',
  border: '1px solid #A7F3D0',
  borderRadius: 10,
  padding: '12px 14px',
  marginBottom: 16,
  color: '#065F46',
  maxWidth: 560,
  marginLeft: 'auto',
  marginRight: 'auto',
};
const offerBannerHeadStyle: React.CSSProperties = { fontWeight: 700, fontSize: 15, lineHeight: 1.35 };
const offerBannerDetailStyle: React.CSSProperties = { fontSize: 14, marginTop: 4, lineHeight: 1.4 };

/** Build the identify() trait map from prefill values.
 *  Spiffy's docs don't pin down the exact property names, so we send name fields
 *  in BOTH camelCase + snake_case variants — whichever Spiffy's loader looks for
 *  will match. Extras are ignored. Same belt-and-suspenders approach for phone. */
function buildIdentifyTraits(
  email: string,
  firstName: string,
  lastName: string,
  params: URLSearchParams,
): Record<string, string> | null {
  if (!email) return null;
  const traits: Record<string, string> = { email, email_address: email };
  if (firstName) {
    traits.first_name = firstName;
    traits.firstName = firstName;
    traits.given_name = firstName;
    traits.givenName = firstName;
  }
  if (lastName) {
    traits.last_name = lastName;
    traits.lastName = lastName;
    traits.family_name = lastName;
    traits.familyName = lastName;
  }
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) traits[k] = v;
  }
  return traits;
}

/** Inject the SpiffyJS shim (no-op if already present). Mirrors the loader from
 *  the Spiffy dashboard / TSBK-Spiffy HubSpot module. The shim queues identify()
 *  / config() calls until the real spiffy.js script hydrates. */
function ensureSpiffyShim(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  const f = (w.spiffy = w.spiffy || []);
  if (f.init || f.invoked) return; // already initialized
  f.invoked = true;
  f.methods = ['identify', 'config', 'reset', 'debug', 'off', 'on'];
  f.factory = (i: string) => function (...args: unknown[]) {
    args.unshift(i);
    f.push(args);
    return f;
  };
  f.methods.forEach((m: string) => {
    w.spiffy[m] = f.factory(m);
  });
  f.load = (acct: string) => {
    if (w.spiffy.ACCOUNT) return; // idempotent — second call is a no-op
    w.spiffy.ACCOUNT = acct;
    const e = document.createElement('script');
    e.type = 'text/javascript';
    e.async = true;
    (e as HTMLScriptElement & { crossorigin: string }).crossorigin = 'anonymous';
    e.src = `https://js.static.spiffy.co/spiffy.js?a=${acct}`;
    const n = document.getElementsByTagName('script')[0];
    n.parentNode?.insertBefore(e, n);
  };
  w.spiffy.SNIPPET_VERSION = '1.1.0';
}

/**
 * Mounts the Spiffy embed. Sequence is critical and matches the TSBK-Spiffy
 * HubSpot module exactly:
 *   1. ensureSpiffyShim() — defines window.spiffy queue if missing
 *   2. spiffy.identify(traits) + spiffy.config({hideSidebar: false}) — queued
 *   3. document.createElement('spiffy-checkout'), setAttribute('url'), append
 *   4. spiffy.load(account) — injects spiffy.js, which scans the DOM and
 *      renders any <spiffy-checkout> elements it finds
 *
 * Because (4) happens AFTER (3), the element is in the DOM when SpiffyJS does
 * its initial scan. spiffy.load() is idempotent — re-mounts (e.g. URL change)
 * call it again but it no-ops after the first, so the account is locked in at
 * first mount. The caller resolves the currency BEFORE mounting for exactly
 * this reason.
 */
export function SpiffyEmbedMount({
  wrapRef,
  url,
  account,
  identifyTraits,
}: {
  wrapRef: React.MutableRefObject<HTMLDivElement | null>;
  url: string;
  account: string;
  identifyTraits: Record<string, string> | null;
}) {
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !url) return;

    // Spiffy copies this page's own query into the form; a spaced name there
    // arrives as "Mary+Ann". Drop it from the address first (spiffyUrl.ts).
    const cleaned = withoutSpacedNameParams(window.location.href);
    if (cleaned) window.history.replaceState(window.history.state, '', cleaned);
    ensureSpiffyShim();

    // Identify + config are queued by the shim; spiffy.js replays them on hydration.
    if (identifyTraits && window.spiffy) window.spiffy.identify(identifyTraits);
    if (window.spiffy) window.spiffy.config({ hideSidebar: false });

    // Idempotent — strip any prior children before append.
    while (wrap.firstChild) wrap.removeChild(wrap.firstChild);

    // Spiffy keeps this page's address on the order; the browser id rides in it
    // so the purchase webhook can tie this browser to the buyer.
    stampVisitorOnAddress();

    const el = document.createElement('spiffy-checkout');
    el.setAttribute('url', url);
    wrap.appendChild(el);

    // Load spiffy.js LAST. By this point the element is in the DOM, so when
    // spiffy.js hydrates and scans, it will find and render our checkout.
    // Account is parsed from the checkout URL's subdomain (per-currency
    // checkouts live in per-region Spiffy accounts).
    if (window.spiffy?.load) window.spiffy.load(account);

    return () => {
      // Cleanup so SPA navigation doesn't leak the embed iframe.
      while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
    };
  }, [wrapRef, url, account, identifyTraits]);
  return null;
}

// ---------- inline styles (TSB brand: Electric Blue #2424E6 + Poppins) ----------

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#F8FAFC',
  padding: '0 0 48px',
  fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const topBarStyle: React.CSSProperties = {
  background: '#ffffff',
  borderBottom: '1px solid #E2E8F0',
  padding: '16px 24px',
  marginBottom: 24,
};

const logoLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
};

const logoImgStyle: React.CSSProperties = {
  height: 40,
  width: 'auto',
  display: 'block',
};

const shellStyle: React.CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: '0 16px',
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 16,
  padding: '40px 36px 32px',
  maxWidth: 480,
  margin: '60px auto',
  boxShadow: '0 18px 40px -12px rgba(15, 23, 42, 0.18)',
};

const embedWrapStyle: React.CSSProperties = {
  // No card chrome — let Spiffy's own form chrome be the visual focus.
  // SpiffyJS sizes itself within the wrap.
};

const footStyle: React.CSSProperties = {
  marginTop: 28,
  textAlign: 'center',
  fontSize: 12.5,
  color: '#94A3B8',
};

const footLinkStyle: React.CSSProperties = {
  color: '#64748B',
  textDecoration: 'underline',
};

// ---------- Ignite theme overlays (opt-in via cfg.pageTheme === 'ignite') ----------
// Token VALUES copied from taker/src/blocks-events.css "IGNITE THEME" (the
// Summit 2026 landing page's dark navy/magenta design) — but this is its OWN
// mechanism, not that stylesheet's `.tsb-evt-theme-ignite` class tree.
// CheckoutPage renders plain inline-styled React with none of the `.tsb-evt-*`
// classNames those CSS rules are scoped under, so reusing the CSS classes
// directly wasn't an option; these overlays merge onto the base style objects
// above via `themed()` instead. The capture-card ITSELF (cardStyle, inputs,
// heading) is deliberately left white/light even in ignite mode — a solid
// light card floating on the dark gradient (the same "dark page, light form"
// pattern used in many payment UIs) keeps every input's default browser
// placeholder/contrast intact with zero extra CSS, which a translucent dark
// card would put at risk. Only the page chrome around it (background, top
// bar, footer) and a few small accent details (price chip, submit button,
// included-list checks) carry the dark palette through.
const ignitePageStyle: Partial<React.CSSProperties> = {
  background:
    'radial-gradient(1100px 620px at 78% 4%, rgba(236,30,150,.16), transparent 68%), ' +
    'radial-gradient(900px 640px at 6% 34%, rgba(59,91,253,.15), transparent 66%), ' +
    'linear-gradient(176deg, #09081C 0%, #140F31 42%, #24123F 74%, #2C1046 100%)',
  backgroundAttachment: 'fixed',
};

const igniteTopBarStyle: Partial<React.CSSProperties> = {
  background: 'transparent',
  borderBottom: '1px solid rgba(255,255,255,.10)',
};

const igniteFootStyle: Partial<React.CSSProperties> = {
  color: 'rgba(255,255,255,.55)',
};

const igniteFootLinkStyle: Partial<React.CSSProperties> = {
  color: 'rgba(255,255,255,.78)',
};

// ---------- capture card + member panel styles ----------

const captureHeadingStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 22,
  fontWeight: 800,
  lineHeight: 1.25,
  letterSpacing: '-0.015em',
  color: '#0F172A',
};

const captureSubheadStyle: React.CSSProperties = {
  margin: '0 0 20px',
  fontSize: 14.5,
  lineHeight: 1.6,
  color: '#64748B',
};

const captureLabelStyle: React.CSSProperties = {
  display: 'block',
  margin: '14px 0 6px',
  fontSize: 13,
  fontWeight: 600,
  color: '#334155',
};

const captureInputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  fontSize: 15,
  fontFamily: 'inherit',
  color: '#0F172A',
  background: '#F8FAFC',
  border: '1.5px solid #E2E8F0',
  borderRadius: 10,
};

const captureSubmitStyle: React.CSSProperties = {
  width: '100%',
  marginTop: 20,
  padding: '14px 20px',
  fontSize: 15,
  fontWeight: 700,
  fontFamily: 'inherit',
  color: '#ffffff',
  background: '#2424E6',
  border: 0,
  borderRadius: 10,
  cursor: 'pointer',
};

// Ignite's primary action colour (--tsb-coral in blocks-events.css) + its
// pill button radius (--tsb-btn-radius: 999px) — the one CaptureCard control
// that carries the Summit accent all the way through the interaction, even
// though the card it sits on stays light.
const igniteCaptureSubmitStyle: Partial<React.CSSProperties> = {
  background: '#EC1E96',
  borderRadius: 999,
};

const capturePriceChipStyle: React.CSSProperties = {
  display: 'inline-block',
  margin: '0 0 14px',
  padding: '5px 12px',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.01em',
  color: '#2424E6',
  background: 'rgba(36, 36, 230, 0.08)',
  borderRadius: 999,
};

const igniteCapturePriceChipStyle: Partial<React.CSSProperties> = {
  color: '#EC1E96',
  background: 'rgba(236,30,150,.08)',
};

const captureIncludedListStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: '0 0 22px',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const captureIncludedItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  fontSize: 14,
  lineHeight: 1.5,
  color: '#334155',
};

const captureIncludedCheckStyle: React.CSSProperties = {
  flexShrink: 0,
  color: '#16A34A',
  fontWeight: 700,
};

// Ignite's secondary accent (--tsb-blue in blocks-events.css) — matches the
// checkmark colour VIP's own pricing card uses on the landing page.
const igniteCaptureIncludedCheckStyle: Partial<React.CSSProperties> = {
  color: '#3B5BFD',
};

const captureReassuranceStyle: React.CSSProperties = {
  margin: '16px 0 0',
  textAlign: 'center',
  fontSize: 12.5,
  color: '#94A3B8',
};

const captureErrorStyle: React.CSSProperties = {
  marginTop: 14,
  padding: '10px 12px',
  background: 'rgba(220,38,38,.06)',
  border: '1px solid rgba(220,38,38,.25)',
  borderRadius: 8,
  fontSize: 13,
  color: '#991B1B',
};

const memberHeadingStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 22,
  fontWeight: 800,
  lineHeight: 1.3,
  letterSpacing: '-0.015em',
  color: '#0F172A',
};

const memberBodyStyle: React.CSSProperties = {
  margin: '0 0 22px',
  fontSize: 14.5,
  lineHeight: 1.65,
  color: '#475569',
};

const memberCtaStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  padding: '14px 20px',
  textAlign: 'center',
  fontSize: 15,
  fontWeight: 700,
  color: '#ffffff',
  background: '#2424E6',
  border: 0,
  borderRadius: 10,
  textDecoration: 'none',
  cursor: 'pointer',
};
